/*
 * Eunice, the AI receptionist (a paid add-on), on his own page in the side menu (owner, 2026-11-01; was under Inbox,
 * Settings). Off, practice (he writes his replies down, nothing is sent) or live; what he is doing now, each reply opening
 * its conversation; what he knows; missed call text-back; this month's replies; his lessons (used once approved).
 */
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import type { EuniceRun, Settings } from "@steward/shared";
import { useAddLesson, useEunice, useJudgeLesson, useSettings, useUpdateSettings } from "../../api/hooks";
import { useSession } from "../../app/session";
import { Choice, InputText } from "../../components/fields";
import { useToast } from "../../components/overlays";
import { ago, Button, Card, Chip, Details, Page, Skeleton, StatusLabel, Switch } from "../../components/parts";
import { errText } from "../../components/work";

/** How Eunice sorted each message, in plain words, so the owner sees what her reply was based on. */
const READ_AS: Record<string, string> = {
  new_inquiry: "a new person asking about the work",
  ready_to_book: "someone ready to book",
  photos_for_quote: "photos for a quote",
  existing_customer: "a current client",
  complaint: "a complaint",
  vendor_or_work_order: "a vendor or a work order",
  job_seeker: "someone looking for a job",
  sales_spam: "a sales pitch",
  auto_reply: "an automatic reply",
  acknowledgement: "a thank you or an OK",
  other: "something else",
};

const MODE_WORDS = { off: "Off", practice: "Practice (writes replies down, sends nothing)", live: "Live" } as const;

type Mode = Settings["receptionist"]["mode"];
const MODE_STATUS = { off: "inactive", practice: "warning", live: "success" } as const;
const MODE_SHORT = { off: "Off", practice: "Practice", live: "Live" } as const;
const MODE_SAYS = {
  off: "He is not answering anyone. Texts wait in the Inbox for the office.",
  practice: "He reads every text and writes the reply he would send. Nothing goes out.",
  live: "He answers texts and missed calls for you, and flags anything the office should see.",
} as const;

/** The side menu page: what he is doing, then his settings and lessons. */
export function ReceptionistPage() {
  const { company } = useSession();
  const s = useSettings(company.id);
  const name = s.data?.settings.receptionist.name ?? "Receptionist";
  return (
    <Page title={name} subtitle="Your text receptionist">
      <div className="stack">
        <StatusCard />
        <ActivityCard />
        <EuniceSettings bare />
      </div>
    </Page>
  );
}

/** Live, practice or off at a glance, with a switch for the owner and the numbers. */
function StatusCard() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const e = useEunice();
  const save = useUpdateSettings();
  const toast = useToast();
  if (!s.data || !e.data) return <Skeleton rows={3} />;
  const r = s.data.settings.receptionist;
  const weekAgo = Date.now() - 7 * 86_400_000;
  const flagged = e.data.runs.filter((x) => x.alert && Date.parse(x.at) > weekAgo).length;
  const today = e.data.runs.filter((x) => new Date(x.at).toDateString() === new Date().toDateString()).length;
  const setMode = (mode: Mode) =>
    save.mutate(
      { receptionist: { mode } },
      {
        onSuccess: () => toast(`${r.name} is ${MODE_SHORT[mode].toLowerCase()}`),
        onError: (x) => toast(errText(x), { kind: "error" }),
      },
    );
  return (
    <Card>
      <div className="rcp-status">
        <div className="rcp-now">
          <StatusLabel status={MODE_STATUS[r.mode]} pill>
            {MODE_SHORT[r.mode]}
          </StatusLabel>
          <p>{MODE_SAYS[r.mode]}</p>
        </div>
        {can("settings.update") ? (
          <div className="chips" role="group" aria-label="Mode">
            {(["live", "practice", "off"] as const).map((m) => (
              <Chip key={m} pressed={r.mode === m} onClick={() => r.mode !== m && setMode(m)}>
                {MODE_SHORT[m]}
              </Chip>
            ))}
          </div>
        ) : null}
        <dl className="rcp-stats">
          <div>
            <dt>Today</dt>
            <dd className="num">{today}</dd>
          </div>
          <div>
            <dt>Replies this month</dt>
            <dd className="num">
              {e.data.usedThisMonth} <span className="muted">of {e.data.allowance}</span>
            </dd>
          </div>
          <div>
            <dt>Flagged this week</dt>
            <dd className="num">{flagged}</dd>
          </div>
        </dl>
      </div>
    </Card>
  );
}

type Show = "all" | "sent" | "practice" | "flagged" | "skipped";
const SHOW_WORDS: Record<Show, string> = {
  all: "All",
  sent: "Sent",
  practice: "Would say",
  flagged: "Flagged",
  skipped: "Left for the office",
};
const shows = (x: EuniceRun, f: Show) =>
  f === "all" ||
  (f === "sent" && x.sent) ||
  (f === "practice" && !x.sent && x.reply !== null) ||
  (f === "flagged" && x.alert) ||
  (f === "skipped" && x.reply === null);

/** What he did, newest first, refreshed every 30 seconds. Each one opens its conversation in the Inbox. */
function ActivityCard() {
  const e = useEunice(30_000);
  const [show, setShow] = useState<Show>("all");
  if (!e.data) return <Skeleton rows={6} />;
  const all = e.data.runs;
  const runs = all.filter((x) => shows(x, show));
  return (
    <Card title="What he is doing" flush>
      <div className="chips rcp-filter">
        {(Object.keys(SHOW_WORDS) as Show[]).map((f) => (
          <Chip key={f} pressed={show === f} onClick={() => setShow(f)} count={all.filter((x) => shows(x, f)).length}>
            {SHOW_WORDS[f]}
          </Chip>
        ))}
      </div>
      {runs.length === 0 ? (
        <p className="muted auto-intro">Nothing here yet.</p>
      ) : (
        <ul className="rcp-feed">
          {runs.slice(0, 100).map((x) => (
            <li key={x.id}>
              <Link to="/inbox" search={{ thread: x.threadId }} className="rcp-run">
                <span className="rcp-who">
                  <b>{x.contact}</b>
                  {x.alert ? <StatusLabel status="warning">Check this</StatusLabel> : null}
                  <span className="muted">{ago(x.at)}</span>
                </span>
                {x.reply ? (
                  <span className={`rcp-reply${x.sent ? "" : " draft"}`}>
                    <span className="rcp-tag">{x.sent ? "Sent" : "Would say"}</span>
                    {x.reply}
                  </span>
                ) : (
                  <span className="muted">{x.skipped ?? "No reply"}</span>
                )}
                {x.category ? <span className="muted">Read it as {READ_AS[x.category] ?? "something else"}.</span> : null}
                {x.alert && x.alertSummary ? <span>Why he flagged it: {x.alertSummary}</span> : null}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** His settings and lessons. bare: on his own page, where the status card and the feed show the rest. */
export function EuniceSettings({ bare = false }: { bare?: boolean }) {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const e = useEunice();
  const save = useUpdateSettings();
  const add = useAddLesson();
  const judge = useJudgeLesson();
  const toast = useToast();
  const [draft, setDraft] = useState<Settings["receptionist"] | null>(null);
  const [lesson, setLesson] = useState("");
  if (!s.data || !e.data) return <Skeleton rows={8} />;
  const r = s.data.settings.receptionist;
  const owner = can("settings.update");

  return (
    <>
      <Card
        id="eunice"
        title={bare ? "What he knows and does" : r.name}
        action={
          owner && !draft ? (
            <Button type="secondary" size="small" icon="edit" onClick={() => setDraft({ ...r })}>
              Edit
            </Button>
          ) : null
        }
      >
        {draft ? (
          <div className="stack">
            <Choice
              label="Mode"
              value={draft.mode}
              options={(["off", "practice", "live"] as const).map((m) => ({ value: m, label: MODE_WORDS[m] }))}
              onChange={(mode) => setDraft({ ...draft, mode })}
            />
            <InputText
              label="His name"
              description="The name he signs with and gives when asked."
              value={draft.name}
              maxLength={30}
              onChange={(x) => setDraft({ ...draft, name: x.target.value })}
            />
            <label className="field wide">
              <span className="label">What he knows about the business (services, areas, hours, how quotes work)</span>
              <textarea rows={8} value={draft.brief} onChange={(x) => setDraft({ ...draft, brief: x.target.value })} />
            </label>
            <label className="field wide">
              <span className="label">Prices he may give (leave empty and he never gives a price)</span>
              <textarea rows={4} value={draft.pricing} onChange={(x) => setDraft({ ...draft, pricing: x.target.value })} />
            </label>
            <InputText
              label="Careers link"
              wide
              value={draft.careersUrl}
              onChange={(x) => setDraft({ ...draft, careersUrl: x.target.value })}
            />
            <InputText
              label="Quote link"
              wide
              value={draft.quoteUrl}
              onChange={(x) => setDraft({ ...draft, quoteUrl: x.target.value })}
            />
            <Switch
              checked={draft.instantQuote}
              label="The quote page prices on the spot"
              onChange={(instantQuote) => setDraft({ ...draft, instantQuote })}
            />
            <Switch
              checked={draft.missedText}
              label="Text back missed calls"
              onChange={(missedText) => setDraft({ ...draft, missedText })}
            />
            <Switch
              checked={draft.noReplyNudge}
              label="Check in once when a new lead goes quiet for a day"
              onChange={(noReplyNudge) => setDraft({ ...draft, noReplyNudge })}
            />
            <InputText
              label="Minutes he waits before he answers"
              type="number"
              min={0}
              max={120}
              value={draft.waitMinutes}
              onChange={(x) =>
                setDraft({ ...draft, waitMinutes: Math.min(120, Math.max(0, Math.round(Number(x.target.value)))) })
              }
              description="Time for you or the team to text back first. If anyone of ours answers, he stays out. 0 means right away."
            />
            <InputText
              label="The word for the work (in the missed call nudge)"
              value={draft.serviceWord}
              onChange={(x) => setDraft({ ...draft, serviceWord: x.target.value })}
            />
            <InputText
              label="Owner phones (texts from these are instructions, never answered)"
              wide
              value={draft.ownerPhones.join(", ")}
              onChange={(x) => setDraft({ ...draft, ownerPhones: x.target.value.split(/[,\s]+/).filter(Boolean) })}
            />
            <div className="row end">
              <Button type="tertiary" onClick={() => setDraft(null)}>
                Cancel
              </Button>
              <Button
                loading={save.isPending}
                onClick={() =>
                  save.mutate(
                    { receptionist: draft },
                    {
                      onSuccess: () => {
                        setDraft(null);
                        toast("Saved");
                      },
                      onError: (x) => toast(errText(x), { kind: "error" }),
                    },
                  )
                }
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <Details
            rows={[
              ["Mode", MODE_WORDS[r.mode]],
              ["Name", r.name],
              ["Replies this month", `${String(e.data.usedThisMonth)} of ${String(e.data.allowance)}`],
              [
                "Waits",
                r.waitMinutes
                  ? `${String(r.waitMinutes)} minutes for the team, then answers if nobody did`
                  : "Answers right away",
              ],
              ["Missed calls", r.missedText ? "Texted back" : "Off"],
              ["Quiet leads", r.noReplyNudge ? "One check in after a day" : "Off"],
              ["Knows", r.brief ? `${String(r.brief.length)} letters about the business` : null],
              ["Phone company webhook", e.data.webhookUrl ?? "Add the Quo key at head office first"],
            ]}
          />
        )}
      </Card>

      <Card title="Lessons" flush>
        <p className="muted auto-intro">
          A lesson is a rule for Eunice, like "Never offer Sunday visits." He uses it once the owner approves it.
        </p>
        <ul className="auto-list">
          {e.data.lessons.map((l) => (
            <li key={l.id} className="setting-row">
              <span className="what">
                <b>{l.text}</b>
              </span>
              {l.status === "proposed" && owner ? (
                <span className="row">
                  <Button size="small" onClick={() => judge.mutate({ id: l.id, status: "approved" })}>
                    Approve
                  </Button>
                  <Button type="tertiary" size="small" onClick={() => judge.mutate({ id: l.id, status: "rejected" })}>
                    Turn down
                  </Button>
                </span>
              ) : (
                <StatusLabel status={l.status === "approved" ? "success" : l.status === "rejected" ? "inactive" : "warning"}>
                  {l.status === "approved" ? "In use" : l.status === "rejected" ? "Turned down" : "Waiting"}
                </StatusLabel>
              )}
            </li>
          ))}
        </ul>
        {can("client.update") ? (
          <div className="row auto-step">
            <InputText label="New lesson" wide value={lesson} onChange={(x) => setLesson(x.target.value)} />
            <Button
              type="secondary"
              disabled={lesson.trim().length < 5}
              onClick={() => add.mutate(lesson.trim(), { onSuccess: () => setLesson("") })}
            >
              Propose
            </Button>
          </div>
        ) : null}
      </Card>

      {bare ? null : (
        <Card title="What Eunice did lately" flush>
          {e.data.runs.length === 0 ? (
            <p className="muted auto-intro">Nothing yet.</p>
          ) : (
            <ul className="auto-list">
              {e.data.runs.slice(0, 50).map((x) => (
                <li key={x.id} className="setting-row">
                  <span className="what">
                    <b>
                      {x.contact} {x.alert ? <StatusLabel status="warning">Check this</StatusLabel> : null}
                    </b>
                    <span>{x.skipped ?? (x.reply ? `${x.sent ? "Sent" : "Would say"}: ${x.reply}` : "No reply")}</span>
                    {x.category ? <span>He read it as {READ_AS[x.category] ?? "something else"}.</span> : null}
                    {x.alert && x.alertSummary ? <span>Why he flagged it: {x.alertSummary}</span> : null}
                  </span>
                  <span className="muted">{ago(x.at)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </>
  );
}
